import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 max-[480px]:gap-1 text-center rounded-sm text-sm leading-none font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        highlight:
          "bg-highlight text-highlight-foreground hover:bg-highlight-600 focus-visible:ring-foreground-800/50 font-[600] font-display tracking-wide",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 max-[480px]:px-2 py-2 has-[>svg]:px-3 has-[>svg]:max-[480px]:px-1.5",
        sm: "h-8 rounded-sm gap-1.5 max-[480px]:gap-1 px-3 max-[480px]:px-1.5 text-xs leading-none has-[>svg]:px-2.5 has-[>svg]:max-[480px]:px-1",
        lg: "h-12 rounded-sm px-8 max-[480px]:px-4 text-base leading-none has-[>svg]:px-6 has-[>svg]:max-[480px]:px-3",
        xl: "h-14 rounded-sm px-10 max-[480px]:px-5 text-lg leading-none has-[>svg]:px-8 has-[>svg]:max-[480px]:px-4",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-lg": "size-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
